import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap');
  *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }

  html, body { height:100%; overflow:hidden; }

  .login-root {
    font-family:'Sora',sans-serif;
    height:100vh;
    overflow:hidden;
    display:grid;
    grid-template-columns:1fr 1fr;
    background:#f0f2f7;
  }

  /* LEFT — decorative */
  .login-left {
    background:#094A9A;
    display:flex; flex-direction:column; justify-content:space-between;
    padding:40px; position:relative; overflow:hidden;
  }
  .login-left::before { content:''; position:absolute; top:-80px; left:-80px; width:360px; height:360px; background:radial-gradient(circle,rgba(255,255,255,.18) 0%,transparent 70%); border-radius:50%; pointer-events:none; }
  .login-left::after  { content:''; position:absolute; bottom:-100px; right:-60px; width:420px; height:420px; background:radial-gradient(circle,rgba(255,255,255,.12) 0%,transparent 70%); border-radius:50%; pointer-events:none; }

  .login-brand { position:relative; z-index:1; }
  .login-logo-img { height:200px; width:auto; object-fit:contain; display:block; margin-top:-10px; margin-left:-40px; }

  .login-left-body { position:relative; z-index:1; flex:1; display:flex; flex-direction:column; justify-content:center; padding:40px 0; }
  .login-headline  { font-size:2.4rem; font-weight:800; color:white; letter-spacing:-.04em; line-height:1.15; margin-bottom:16px; }
  .login-headline span { color:rgba(255,255,255,.85); -webkit-text-fill-color:unset; background:none; }
  .login-desc { font-size:.85rem; color:rgba(255,255,255,.75); line-height:1.65; max-width:320px; }
  .login-features { display:flex; flex-direction:column; gap:10px; margin-top:32px; }
  .login-feature { display:flex; align-items:center; gap:10px; font-size:.78rem; color:rgba(255,255,255,.85); }
  .login-feature-dot { width:6px; height:6px; border-radius:50%; flex-shrink:0; }
  .login-left-footer { position:relative; z-index:1; font-family:'JetBrains Mono',monospace; font-size:.68rem; color:rgba(255,255,255,.4); }

  /* RIGHT — form */
  .login-right { display:flex; align-items:center; justify-content:center; padding:40px 20px; background:#f0f2f7; }
  .login-card  { width:100%; max-width:400px; animation:loginCardIn .4s ease both; }
  @keyframes loginCardIn { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }

  .login-card-header { margin-bottom:28px; }
  .login-card-title  { font-size:1.5rem; font-weight:800; color:#0f1123; letter-spacing:-.03em; line-height:1.2; }
  .login-card-sub    { font-size:.78rem; color:#9094a8; margin-top:6px; }

  .login-box { background:white; border-radius:20px; border:1px solid #e8ebf4; padding:28px; box-shadow:0 4px 24px rgba(0,0,0,.06); }
  .login-field { display:flex; flex-direction:column; gap:6px; margin-bottom:16px; }
  .login-label { font-size:.7rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; }
  .login-input-wrap { position:relative; }
  .login-input { font-family:'Sora',sans-serif; font-size:.85rem; width:100%; padding:11px 14px; border:1.5px solid #e0e4f0; border-radius:12px; background:#f8f9fc; color:#1a1d2e; outline:none; transition:border-color .15s,background .15s,box-shadow .15s; }
  .login-input:focus { border-color:#5BA1F5; background:white; box-shadow:0 0 0 3px rgba(91,161,245,.12); }
  .login-input.has-toggle { padding-right:44px; }
  .pw-toggle { position:absolute; right:12px; top:50%; transform:translateY(-50%); width:28px; height:28px; display:flex; align-items:center; justify-content:center; cursor:pointer; color:#b0b5cc; border-radius:7px; transition:color .13s,background .13s; border:none; background:none; }
  .pw-toggle:hover { color:#6b7194; background:#f0f2f7; }
  .pw-toggle svg { width:16px; height:16px; stroke-width:1.8; }

  .login-submit { width:100%; padding:12px; margin-top:8px; background:#094A9A; color:white; border:none; border-radius:12px; font-family:'Sora',sans-serif; font-size:.88rem; font-weight:700; cursor:pointer; letter-spacing:.01em; transition:box-shadow .2s,transform .15s,background .15s; position:relative; overflow:hidden; }
  .login-submit:hover   { background:#0C5FC6; box-shadow:0 6px 20px rgba(91,161,245,.45); transform:translateY(-1px); }
  .login-submit:active  { transform:scale(.98); }
  .login-submit:disabled{ opacity:.7; cursor:not-allowed; transform:none; }

  .login-footer-note { text-align:center; margin-top:18px; font-size:.72rem; color:#b0b5cc; }
  .login-dev-link { color:#094A9A; font-weight:600; text-decoration:none; transition:opacity .15s; }
  .login-dev-link:hover { opacity:.7; text-decoration:underline; }

  .btn-spinner { display:inline-block; width:15px; height:15px; border:2px solid rgba(255,255,255,.3); border-top-color:white; border-radius:50%; animation:spin .7s linear infinite; vertical-align:middle; margin-right:8px; }
  @keyframes spin { to{transform:rotate(360deg);} }

  /* Mobile logo — hidden on desktop */
  .login-mobile-logo { display:none; }

  /* ── Responsive ── */
  @media (max-width:768px) {
    .login-root {
      grid-template-columns: 1fr;
      grid-template-rows: auto 1fr;
      height: 100vh;
      overflow: hidden;
      background: #f0f2f7;
    }

    .login-left { display:none; }

    .login-right {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      justify-content: flex-start;
      padding: 0;
      height: 100%;
      overflow: hidden;
      background: #f0f2f7;
    }

    .login-card {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 100%;
      flex: 1;
    }

    /* Blue logo banner — flush to very top */
    .login-mobile-logo {
      display: flex;
      justify-content: center;
      align-items: center;
      background: #094A9A;
      width: 100%;
      padding: 36px 24px 32px 24px;
      position: relative;
      overflow: hidden;
      /* extend behind status bar on notched phones */
      margin-top: 0;
    }

    /* subtle glow circles same as desktop left panel */
    .login-mobile-logo::before {
      content: '';
      position: absolute;
      top: -40px; left: -40px;
      width: 200px; height: 200px;
      background: radial-gradient(circle,rgba(255,255,255,.18) 0%,transparent 70%);
      border-radius: 50%;
      pointer-events: none;
    }
    .login-mobile-logo::after {
      content: '';
      position: absolute;
      bottom: -50px; right: -30px;
      width: 220px; height: 220px;
      background: radial-gradient(circle,rgba(255,255,255,.12) 0%,transparent 70%);
      border-radius: 50%;
      pointer-events: none;
    }

    .login-mobile-logo img {
      height: 64px;
      width: auto;
      object-fit: contain;
      position: relative;
      z-index: 1;
    }

    /* Form area */
    .login-form-area {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      flex: 1;
      width: 100%;
      padding: 32px 20px 28px 20px;
    }

    .login-card-header {
      text-align: center;
      margin-bottom: 24px;
      width: 100%;
    }

    .login-card-title { font-size: 1.35rem; }
    .login-card-sub   { font-size: .76rem; }

    .login-box {
      width: 100%;
      padding: 22px 18px;
      border-radius: 18px;
    }

    .login-footer-note {
      text-align: center;
      width: 100%;
      margin-top: 20px;
    }
  }

  @media (max-width:380px) {
    .login-mobile-logo { padding: 28px 16px 24px 16px; }
    .login-mobile-logo img { height: 54px; }
    .login-form-area { padding: 24px 14px 20px 14px; }
    .login-box { padding: 18px 14px; border-radius: 14px; }
    .login-input { font-size: .82rem; padding: 10px 12px; }
    .login-submit { font-size: .84rem; padding: 11px; }
    .login-card-title { font-size: 1.2rem; }
  }
`;

const Login = () => {
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [showPw,   setShowPw]   = useState(false);
  const [loading,  setLoading]  = useState(false);
  const { login } = useAuth();
  const navigate  = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const success = await login(email, password);
    setLoading(false);
    if (success) navigate('/dashboard');
  };

  const features = [
    { label:'Manage students, teachers & classes', color:'rgba(255,255,255,.9)' },
    { label:'Track attendance & fee payments',     color:'#a8d8ff' },
    { label:'Schedule exams and publish results',  color:'rgba(255,255,255,.9)' },
    { label:'Generate reports and send notices',   color:'#a8d8ff' },
  ];

  return (
    <>
      <style>{style}</style>
      <div className="login-root">

        {/* Left panel — desktop only */}
        <div className="login-left">
          <div className="login-brand">
            <img src="/assets/logo.png" alt="Logo" className="login-logo-img" />
          </div>
          <div className="login-left-body">
            <div className="login-headline">Everything you need to<br /><span>run your school.</span></div>
            <p className="login-desc">A complete school management system built for administrators. Streamline your daily operations from one dashboard.</p>
            <div className="login-features">
              {features.map((f,i) => (
                <div key={i} className="login-feature">
                  <div className="login-feature-dot" style={{background:f.color}} />
                  {f.label}
                </div>
              ))}
            </div>
          </div>
          <div className="login-left-footer">© {new Date().getFullYear()} School Portal · v1.0.0</div>
        </div>

        {/* Right — form */}
        <div className="login-right">
          <div className="login-card">

            {/* Mobile logo banner — flush to top, blue bg */}
            <div className="login-mobile-logo">
              <img src="/assets/logo.png" alt="Logo" />
            </div>

            {/* Wrap header + form + footer so they center nicely */}
            <div className="login-form-area">
              <div className="login-card-header">
                <h1 className="login-card-title">Welcome back 👋</h1>
                <p className="login-card-sub">Sign in to your account to continue.</p>
              </div>

              <div className="login-box">
                <form onSubmit={handleSubmit}>
                  <div className="login-field">
                    <label className="login-label">Email Address</label>
                    <div className="login-input-wrap">
                      <input type="email" className="login-input" placeholder="admin@school.edu"
                        value={email} onChange={e=>setEmail(e.target.value)} required autoComplete="email" />
                    </div>
                  </div>
                  <div className="login-field" style={{marginBottom:20}}>
                    <label className="login-label">Password</label>
                    <div className="login-input-wrap">
                      <input type={showPw?'text':'password'} className="login-input has-toggle" placeholder="••••••••"
                        value={password} onChange={e=>setPassword(e.target.value)} required autoComplete="current-password" />
                      <button type="button" className="pw-toggle" onClick={()=>setShowPw(v=>!v)} tabIndex={-1}>
                        {showPw ? <EyeSlashIcon /> : <EyeIcon />}
                      </button>
                    </div>
                  </div>
                  <button type="submit" className="login-submit" disabled={loading}>
                    {loading && <span className="btn-spinner" />}
                    {loading ? 'Signing in…' : 'Sign in'}
                  </button>
                </form>
              </div>

              <div className="login-footer-note">
                Developed by{' '}
                <a href="https://nastouch.com/" target="_blank" rel="noreferrer" className="login-dev-link">
                  Nastouch
                </a>
              </div>
            </div>

          </div>
        </div>

      </div>
    </>
  );
};

export default Login;